import { Icon } from "./Icon";
import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { voiceBucket } from "../routes/inboxSurfaces";
import { nextVoiceSpeed, voiceClock, voiceProgress, voiceSpeedLabel, voiceSpeeds, waveformBars } from "../lib/messages";
import { isMissingObject } from "../lib/reviewMedia";

/* W72 step 6 — A SENT VOICE NOTE: play · waveform · `0:38 / 1:42` · a speed chip (1× · 1.5× · 2×, remembered).
 *
 * ⚠️ THE BUCKET IS PRIVATE, so the note is fetched through a SIGNED URL minted for this reader — which the (staged)
 * policy admits only for the two parties of the message. Nothing here builds a public URL, because none exists.
 * ⚠️ PLAYBACK RESUMES WHERE IT WAS PAUSED: the `<audio>` element lives as long as the bubble, and pausing does not
 * rewind it. ⚠️ THE WAVEFORM IS DECODED FROM THE NOTE ITSELF (`waveformBars`), not stored — the model has no column
 * for it, and a column for it would be a second copy of the recording that could disagree with it.
 * ⚠️ A NOTE WHOSE BUCKET IS NOT LIVE (or that cannot be fetched) SAYS SO, and its play button is disabled — never a
 * button that does nothing.
 * 🔴 W82 STEP 5 — THE SAME PLAYER FOR THE REVIEW: `bucket` says where the note lives (`coach-review-media` for a review, a
 * comment and its thread — `reviewMedia.mediaHome`), and a note whose OBJECT is gone (its owner removed it; the row keeps
 * its path) reads `removedWords` rather than "could not be fetched". `compact` draws it inside a review container:
 * play · `0:32`, no waveform, no speed chip — the same component, never a second player.
 */

const SPEED_KEY = "ht-voice-speed";
const BAR_COUNT = 36;

function readSpeed(): number {
  try {
    const n = Number(localStorage.getItem(SPEED_KEY));
    return (voiceSpeeds as readonly number[]).includes(n) ? n : 1;
  } catch {
    return 1;
  }
}

export type Load = { status: "idle" | "loading" } | { status: "ready"; url: string; bars: number[] } | { status: "error"; message: string };

export function VoiceNote({ path, seconds, enabled, bucket = voiceBucket, removedWords = "Removed", compact = false }: {
  path: string; seconds: number; enabled: boolean; bucket?: string; removedWords?: string; compact?: boolean;
}) {
  const [load, setLoad] = useState<Load>({ status: "idle" });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoad({ status: "loading" });
    void (async () => {
      const signed = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
      if (cancelled) return;
      if (signed.error !== null && isMissingObject(signed.error)) { setLoad({ status: "error", message: removedWords }); return; }
      if (signed.error || !signed.data) { setLoad({ status: "error", message: "This voice note could not be fetched." }); return; }
      try {
        const res = await fetch(signed.data.signedUrl);
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([buf], { type: "audio/mp4" }));
        const bars = await decodeBars(buf);
        if (!cancelled) setLoad({ status: "ready", url: objectUrl, bars });
      } catch {
        if (!cancelled) setLoad({ status: "error", message: "This voice note could not be fetched." });
      }
    })();
    return () => { cancelled = true; if (objectUrl !== null) URL.revokeObjectURL(objectUrl); };
  }, [path, enabled, bucket, removedWords]);

  const unavailable = !enabled ? "Voice notes are not switched on yet." : load.status === "error" ? load.message : null;
  return <VoicePlayer load={load} seconds={seconds} unavailable={unavailable} compact={compact} />;
}

/** The note's waveform, decoded from the recording itself (never stored — a column for it would be a second copy of the
 *  recording that could disagree with it). A note the browser can play but not decode keeps a flat waveform. */
export async function decodeBars(buf: ArrayBuffer): Promise<number[]> {
  try {
    const ctx = new AudioContext();
    const decoded = await ctx.decodeAudioData(buf.slice(0));
    const bars = waveformBars(decoded.getChannelData(0), BAR_COUNT);
    void ctx.close();
    return bars;
  } catch {
    return new Array<number>(BAR_COUNT).fill(0.3);
  }
}

/**
 * 🔴 W72 STEP 7 — THE ONE PLAYER: play · waveform · `0:38 / 1:42` · the speed chip. A SENT note (`VoiceNote`, fetched
 * through a signed URL) and an UNSENT one (the recorder's preview, from its Blob) are drawn by this same component, so
 * the preview is the bubble the client will hear rather than the browser's own `<audio controls>` (a system pill,
 * step 6's finding 5). ⚠️ THE `<audio>` ELEMENT HAS NO `controls` — it is the engine, never the picture.
 */
export function VoicePlayer({ load, seconds, unavailable, compact = false }: { load: Load; seconds: number; unavailable: string | null; compact?: boolean }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [speed, setSpeed] = useState(readSpeed);

  useEffect(() => {
    if (audio.current !== null) audio.current.playbackRate = speed;
  }, [speed, load.status]);

  function toggle() {
    const el = audio.current;
    if (el === null || load.status !== "ready") return;
    if (el.paused) void el.play(); else el.pause();
  }

  function cycleSpeed() {
    const next = nextVoiceSpeed(speed);
    setSpeed(next);
    try { localStorage.setItem(SPEED_KEY, String(next)); } catch { /* per-browser convenience only */ }
  }

  const bars = load.status === "ready" ? load.bars : new Array<number>(BAR_COUNT).fill(0.18);
  const played = seconds > 0 ? Math.round((position / seconds) * BAR_COUNT) : 0;

  return (
    <div className={compact ? "inbox-voice is-compact" : "inbox-voice"}>
      <button type="button" className="inbox-voice-play" onClick={toggle}
              disabled={load.status !== "ready"}
              aria-label={playing ? "Pause voice note" : "Play voice note"}>
        <Icon name={playing ? "pause" : "play"} />
      </button>
      {!compact && (
        <span className="inbox-voice-wave" aria-hidden="true">
          {bars.map((h, i) => (
            <span key={i} className={i < played ? "inbox-voice-bar is-played" : "inbox-voice-bar"}
                  style={{ height: `${String(Math.round(4 + h * 18))}px` }} />
          ))}
        </span>
      )}
      <span className="inbox-voice-time">{compact ? (playing ? voiceClock(position) : voiceClock(seconds)) : voiceProgress(position, seconds)}</span>
      {!compact && (
        <button type="button" className="inbox-voice-speed" onClick={cycleSpeed}
                aria-label={`Playback speed ${voiceSpeedLabel(speed)}. Change speed`}>
          {voiceSpeedLabel(speed)}
        </button>
      )}
      {unavailable !== null && <span className="inbox-voice-note">{unavailable}</span>}
      {load.status === "ready" && (
        <audio ref={audio} src={load.url} preload="auto"
               onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
               onEnded={() => { setPlaying(false); }}
               onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)} />
      )}
    </div>
  );
}
